import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTheme } from "../ThemeContext";
import ImpersonationBanner from "./ImpersonationBanner";
import { useInactivityTimer } from "../hooks/useInactivityTimer";
import api from "../api";
import "./Layout.css";

const navItems = [
  { path: "/dashboard",   label: "Dashboard",    icon: "◈", roles: ["loan_officer","manager","admin","executive"] },
  { path: "/loans",       label: "Loans",         icon: "⊟", roles: ["loan_officer","manager","admin","executive"] },
  { path: "/commissions", label: "Commissions",   icon: "$", roles: ["loan_officer","manager","admin","executive"] },
  { path: "/exceptions",  label: "Exceptions",    icon: "⚑", roles: ["loan_officer","manager","admin","executive"] },
  { path: "/tiers",       label: "Pricing Tiers", icon: "◎", roles: ["loan_officer","manager","admin","executive"] },
  { path: "/upload",      label: "Upload Data",   icon: "↑", roles: ["admin"] },
];

const PAGE_TITLES = {
  "/dashboard":        "Dashboard",
  "/loans":            "Loans",
  "/commissions":      "Commissions",
  "/exceptions":       "Exceptions",
  "/tiers":            "Pricing Tiers",
  "/upload":           "Upload Data",
  "/users":            "Users",
  "/loan-officers":    "Loan Officers",
  "/invite":           "Invite Users",
  "/settings/payroll": "Company Settings",
};

const settingsItems = [
  { path: "/users",            label: "Users",            icon: "⊕", roles: ["admin","manager"] },
  { path: "/loan-officers",    label: "Loan Officers",    icon: "◑", roles: ["admin"] },
  { path: "/invite",           label: "Invite Users",     icon: "✉", roles: ["admin","manager"] },
  { path: "/settings/payroll", label: "Company Settings", icon: "⚙", roles: ["admin"] },
];

const SETTINGS_PATHS = settingsItems.map(s => s.path);

function InactivityWarning({ secondsLeft, onStayLoggedIn, onLogoutNow }) {
  return (
    <div className="inactivity-overlay">
      <div className="inactivity-modal">
        <div className="inactivity-icon">⏱</div>
        <h2 className="inactivity-title">Still there?</h2>
        <p className="inactivity-body">
          You'll be logged out automatically in
        </p>
        <div className="inactivity-countdown">{secondsLeft}s</div>
        <p className="inactivity-body" style={{ marginTop: 0 }}>
          due to inactivity.
        </p>
        <div className="inactivity-actions">
          <button className="btn-primary" onClick={onStayLoggedIn}>
            Stay Logged In
          </button>
          <button className="btn-secondary" onClick={onLogoutNow}>
            Log Out Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, impersonating, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location  = useLocation();
  const navigate  = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [timeoutMinutes, setTimeoutMinutes] = useState(null);

  const isSettingsActive = SETTINGS_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + "/"));
  const [settingsOpen, setSettingsOpen] = useState(isSettingsActive);

  // Fetch inactivity timeout from settings (all roles can read this endpoint)
  useEffect(() => {
    api.get("/payroll/settings")
      .then(r => setTimeoutMinutes(r.data.inactivity_timeout_minutes ?? 30))
      .catch(() => setTimeoutMinutes(30));
  }, []);

  // Update browser tab title on navigation
  useEffect(() => {
    const label = Object.entries(PAGE_TITLES).find(([prefix]) =>
      location.pathname === prefix || location.pathname.startsWith(prefix + "/")
    )?.[1];
    document.title = label ? `${label} | CommPro` : "CommPro";
  }, [location.pathname]);

  const handleLogout = useCallback(() => { logout(); navigate("/login"); }, [logout, navigate]);

  const { warningVisible, secondsLeft, resetTimer } = useInactivityTimer(timeoutMinutes, handleLogout);

  const allowed = navItems.filter(n => n.roles.includes(user?.role));
  const allowedSettings = settingsItems.filter(n => n.roles.includes(user?.role));

  const handleSettingsClick = () => {
    if (collapsed) {
      setCollapsed(false);
      setSettingsOpen(true);
    } else {
      setSettingsOpen(v => !v);
    }
  };

  return (
    <div className={`layout ${collapsed ? "collapsed" : ""} ${impersonating ? "has-banner" : ""}`}>
      <ImpersonationBanner />

      {warningVisible && (
        <InactivityWarning
          secondsLeft={secondsLeft}
          onStayLoggedIn={resetTimer}
          onLogoutNow={handleLogout}
        />
      )}

      <aside className="sidebar">
        <div className="sidebar-header">
          {!collapsed && (
            <div className="logo">
              <div className="logo-rose">⬡</div>
              <div className="logo-text">
                <span className="logo-name">CommPro</span>
                <span className="logo-tagline">by Rosegate</span>
              </div>
            </div>
          )}
          {collapsed && <div className="logo-rose-sm">⬡</div>}
          <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? "›" : "‹"}
          </button>
        </div>
        <nav className="sidebar-nav">
          {allowed.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path || location.pathname.startsWith(item.path + "/") ? "active" : ""}`}
            >
              <span className="nav-icon">{item.icon}</span>
              {!collapsed && <span className="nav-label">{item.label}</span>}
            </Link>
          ))}

          {allowedSettings.length > 0 && (
            <div className="nav-group">
              <button
                className={`nav-item nav-group-trigger ${isSettingsActive ? "active" : ""}`}
                onClick={handleSettingsClick}
                title={collapsed ? "Settings" : undefined}
              >
                <span className="nav-icon">⚙</span>
                {!collapsed && (
                  <>
                    <span className="nav-label">Settings</span>
                    <span className={`nav-group-arrow ${settingsOpen ? "open" : ""}`}>‹</span>
                  </>
                )}
              </button>

              {!collapsed && settingsOpen && (
                <div className="nav-subitems">
                  {allowedSettings.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-item nav-subitem ${location.pathname === item.path || location.pathname.startsWith(item.path + "/") ? "active" : ""}`}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <span className="nav-label">{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleTheme} title="Toggle light/dark mode">
            <span className="theme-icon">{theme === "light" ? "🌙" : "☀️"}</span>
            {!collapsed && <span>{theme === "light" ? "Dark Mode" : "Light Mode"}</span>}
          </button>
          <div className="user-info">
            <div className="user-avatar">{user?.full_name?.[0]?.toUpperCase()}</div>
            {!collapsed && (
              <div className="user-details">
                <div className="user-name">{user?.full_name}</div>
                <div className="user-role">{user?.role?.replace("_", " ")}</div>
              </div>
            )}
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            {collapsed ? "↩" : "Sign Out"}
          </button>
        </div>
      </aside>
      <main className="main-content">{children}</main>
    </div>
  );
}
